import React, { useEffect, useState } from 'react';
import { AlertTriangle, User as UserIcon, TrendingUp, DollarSign } from 'lucide-react';
import { AppModal } from '../base/AppModal';
import { AppInput } from '../base/AppInput';
import { AppButton } from '../base/AppButton';
import { AppCard } from '../base/AppCard';
import type { User } from '../../types';
// ============================================================================
// USER DETAIL PANEL
// ============================================================================
interface UserDetailPanelProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
}
export function UserDetailPanel({
  isOpen,
  user,
  onClose
}: UserDetailPanelProps) {
  if (!user) return null;
  // Mock data for user stats
  const userStats = {
    totalConversations: 127,
    totalCost: 45.3,
    mostUsedAgent: 'Assistente Clienti',
    avgConversationsPerDay: 8.5
  };
  return <AppModal isOpen={isOpen} onClose={onClose} title="Dettaglio Utente" data-id="element-548">
      <div className="space-y-6" data-id="element-549">
        {/* User Info */}
        <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-dark-subtle rounded-xl" data-id="element-550">
          <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-dark-medium flex items-center justify-center" data-id="element-551">
            <UserIcon className="w-8 h-8 text-gray-500 dark:text-gray-400" data-id="element-552" />
          </div>
          <div className="flex-1" data-id="element-553">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-id="element-554">
              {user.nome} {user.cognome}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-555">
              {user.email}
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4" data-id="element-556">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl" data-id="element-557">
            <div className="flex items-center gap-2 mb-2" data-id="element-558">
              <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" data-id="element-559" />
              <span className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase" data-id="element-560">
                Conversazioni
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100" data-id="element-561">
              {userStats.totalConversations}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1" data-id="element-562">
              ~{userStats.avgConversationsPerDay}/giorno
            </p>
          </div>

          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl" data-id="element-563">
            <div className="flex items-center gap-2 mb-2" data-id="element-564">
              <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" data-id="element-565" />
              <span className="text-xs font-medium text-green-600 dark:text-green-400 uppercase" data-id="element-566">
                Costo Totale
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100" data-id="element-567">
              €{userStats.totalCost.toFixed(2)}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1" data-id="element-568">
              Questo mese
            </p>
          </div>
        </div>

        {/* Most Used Agent */}
        <div data-id="element-569">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3" data-id="element-570">
            App AI più utilizzata
          </h4>
          <div className="p-3 bg-gray-50 dark:bg-dark-subtle rounded-xl" data-id="element-571">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-572">
              {userStats.mostUsedAgent}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1" data-id="element-573">
              85% delle conversazioni
            </p>
          </div>
        </div>

        {/* Notes */}
        {user.note && <div data-id="element-574">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" data-id="element-575">
              Note
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 p-3 bg-gray-50 dark:bg-dark-subtle rounded-xl" data-id="element-576">
              {user.note}
            </p>
          </div>}

        <AppButton onClick={onClose} fullWidth data-id="element-577">
          Chiudi
        </AppButton>
      </div>
    </AppModal>;
}
// ============================================================================
// USER EDIT MODAL
// ============================================================================
interface UserEditModalProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
  onSave: (id: string, data: Partial<User>) => Promise<void>;
}
export function UserEditModal({
  isOpen,
  user,
  onClose,
  onSave
}: UserEditModalProps) {
  const [nome, setNome] = useState(user?.nome || '');
  const [cognome, setCognome] = useState(user?.cognome || '');
  const [email, setEmail] = useState(user?.email || '');
  const [note, setNote] = useState(user?.note || '');
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    if (user) {
      setNome(user.nome);
      setCognome(user.cognome);
      setEmail(user.email);
      setNote(user.note || '');
    }
  }, [user]);
  const handleSave = async () => {
    if (!user) return;
    setIsSaving(true);
    try {
      await onSave(user.id, {
        nome,
        cognome,
        email,
        note
      });
      onClose();
    } catch (error) {
      console.error('Failed to save user', error);
    } finally {
      setIsSaving(false);
    }
  };
  return <AppModal isOpen={isOpen} onClose={onClose} title="Modifica Utente" data-id="element-578">
      <div className="space-y-4" data-id="element-579">
        <div className="grid grid-cols-2 gap-4" data-id="element-580">
          <AppInput label="Nome" value={nome} onChange={e => setNome(e.target.value)} placeholder="Mario" data-id="element-581" />
          <AppInput label="Cognome" value={cognome} onChange={e => setCognome(e.target.value)} placeholder="Rossi" data-id="element-582" />
        </div>

        <AppInput label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="mario.rossi@example.com" data-id="element-583" />

        <div data-id="element-584">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-585">
            Note
          </label>
          <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Note aggiuntive sull'utente..." rows={3} className="w-full bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle text-gray-700 dark:text-gray-300 py-2.5 px-3.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none" data-id="element-586" />
        </div>

        <div className="flex gap-3 pt-4" data-id="element-587">
          <AppButton variant="secondary" onClick={onClose} fullWidth data-id="element-588">
            Annulla
          </AppButton>
          <AppButton onClick={handleSave} fullWidth disabled={isSaving || !nome || !cognome || !email} data-id="element-589">
            {isSaving ? 'Salvataggio...' : 'Salva modifiche'}
          </AppButton>
        </div>
      </div>
    </AppModal>;
}
// ============================================================================
// USER DELETE MODAL
// ============================================================================
interface UserDeleteModalProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
  onDelete: (id: string) => Promise<void>;
}
export function UserDeleteModal({
  isOpen,
  user,
  onClose,
  onDelete
}: UserDeleteModalProps) {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const handleDelete = async () => {
    if (!user) return;
    setIsDeleting(true);
    try {
      await onDelete(user.id);
      setConfirmText('');
      onClose();
    } catch (error) {
      console.error('Failed to delete user', error);
    } finally {
      setIsDeleting(false);
    }
  };
  const canDelete = confirmText.toLowerCase() === 'elimina';
  return <AppModal isOpen={isOpen} onClose={onClose} title="Elimina Utente" data-id="element-590">
      <div className="space-y-4" data-id="element-591">
        <div className="flex items-start gap-3 p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-900/30 rounded-xl" data-id="element-592">
          <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" data-id="element-593" />
          <div data-id="element-594">
            <p className="text-sm font-medium text-rose-900 dark:text-rose-200 mb-1" data-id="element-595">
              Attenzione: questa azione è irreversibile
            </p>
            <p className="text-xs text-rose-700 dark:text-rose-300" data-id="element-596">
              Stai per eliminare l'utente "{user?.nome} {user?.cognome}".
              Verranno eliminati anche tutti i dati associati (conversazioni,
              storico, ecc.).
            </p>
          </div>
        </div>

        <div data-id="element-597">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-598">
            Per confermare, digita <strong data-id="element-599">ELIMINA</strong>
          </label>
          <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="Digita ELIMINA" className="w-full bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle text-gray-700 dark:text-gray-300 py-2.5 px-3.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" data-id="element-600" />
        </div>

        <div className="flex gap-3 pt-4" data-id="element-601">
          <AppButton variant="secondary" onClick={onClose} fullWidth data-id="element-602">
            Annulla
          </AppButton>
          <AppButton onClick={handleDelete} fullWidth disabled={isDeleting || !canDelete} className="bg-rose-600 hover:bg-rose-700 text-white disabled:bg-gray-300 disabled:text-gray-500" data-id="element-603">
            {isDeleting ? 'Eliminazione...' : 'Elimina definitivamente'}
          </AppButton>
        </div>
      </div>
    </AppModal>;
}